import { useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { Box, Grid } from '@mui/material';
import { useSearchUserNotifications, useUpdateUserNotificationStatus } from '@services/notification';
import { useCurrentUser } from '@services/user';
import { NoNotifications } from '@components/notification/NoNotifications';
import { Notifications } from '@components/notification/Notifications';
import { NotificationPanelHeader } from '@components/notification/NotificationPanelHeader';
import Loading from '@components/Loading';
import { NotificationStatus, UserNotification } from '@models/notification';

type NotificationsPanelProps = {
  onClosePopover: () => void;
  unseenNotificationsCount: number;
};

export const NotificationPanel = (props: NotificationsPanelProps) => {
  const { onClosePopover, unseenNotificationsCount } = props;
  const [isScrollToBottom, setIsScrollToBottom] = useState(false);
  const { currentUser } = useCurrentUser();
  const {
    isPending: isLoading,
    isFetchingNextPage,
    notifications,
    fetchNextPage,
  } = useSearchUserNotifications(false, currentUser?.id, 10);

  const { mutateNotificationStatus } = useUpdateUserNotificationStatus();
  const allNotifications = notifications?.pages
    ?.flatMap<UserNotification>((page) => page.items)
    .sort((a, b) => {
      if (!a?.createdAt || !b?.createdAt) return 0;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  const allNotificationIds = allNotifications?.map((n) => n?.id as string);
  const unseenNotificationIds = allNotifications?.filter((i) => !i?.seenAt).map((n) => n?.id as string);
  const hasNotifications = !!allNotifications?.length;

  const hasMoreNotifications = notifications?.pages?.[0]?.pagination?.total !== 0;
  const handleNotificationsChange = (status: NotificationStatus, notificationIds?: string[]) => {
    if (!notificationIds?.length) return;

    const requestBody = {
      userId: currentUser?.id,
      status,
      notificationIds,
    };
    mutateNotificationStatus(requestBody);
  };

  useEffect(() => {
    handleNotificationsChange(NotificationStatus.Seen, unseenNotificationIds);
    // eslint-disable-next-line
  }, [notifications?.pages]);

  useEffect(() => {
    if (isScrollToBottom) {
      fetchNextPage();
    }
    // eslint-disable-next-line
  }, [isScrollToBottom]);

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <NotificationPanelHeader
        onClosePopover={onClosePopover}
        onDismissAll={() => handleNotificationsChange(NotificationStatus.Dismissed, allNotificationIds)}
        badgeCount={unseenNotificationsCount}
        isDisabled={!hasNotifications || isLoading || isFetchingNextPage}
      />
      <Grid
        container
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          maxHeight: '532px',
          minHeight: '150px',
          overflowY: 'auto',
          scrollbarColor: `grey transparent`,
        }}
      >
        {(isLoading || isFetchingNextPage) && <Loading />}
        {!isLoading &&
          (hasNotifications ? (
            <>
              <Notifications
                onClosePopover={onClosePopover}
                notifications={allNotifications}
                onNotificationChange={handleNotificationsChange}
              />
              <InView id='scrollToBottomObserver' as='div' onChange={(inView) => setIsScrollToBottom(inView)} />
            </>
          ) : !hasMoreNotifications ? (
            <NoNotifications />
          ) : null)}
      </Grid>
    </Box>
  );
};
